import React, { useEffect, useState } from 'react';
import { Film, Play, Download, X, Volume2, Calendar, FolderKanban, BookOpen } from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { RenderOutput } from '../types/index.js';
import { formatMediaUrl } from '../utils/media.js';

export const VideoLibrary: React.FC = () => {
  const [videos, setVideos] = useState<RenderOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<RenderOutput | null>(null);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const res = await api.getVideos();
      setVideos(res.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  return (
    <div>
      <Header
        title="Biblioteca de Vídeos"
        subtitle="Assista e faça download das suas videoaulas profissionais produzidas em MP4"
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {loading ? (
          <div className="p-12 text-center text-xs text-txt-muted">Carregando biblioteca...</div>
        ) : videos.length === 0 ? (
          <EduCard>
            <div className="text-center py-12">
              <Film size={36} className="mx-auto text-txt-muted mb-2" />
              <h3 className="text-sm font-semibold text-txt-primary">Nenhum vídeo concluído na biblioteca</h3>
              <p className="text-xs text-txt-secondary mt-1">
                Suas videoaulas renderizadas com sucesso aparecerão automaticamente nesta seção.
              </p>
            </div>
          </EduCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((vid) => (
              <EduCard
                key={vid.id}
                title={vid.lessonTitle || `Videoaula — ${vid.lessonId.substring(0, 10)}`}
                subtitle={
                  <div className="flex flex-col gap-0.5 mt-0.5">
                    {vid.projectName && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-primary">
                        <FolderKanban size={13} className="shrink-0" />
                        {vid.projectName}
                      </span>
                    )}
                    <span className="text-[11px] text-txt-muted">
                      Resolução: {vid.resolution}
                    </span>
                  </div>
                }
                footer={
                  <div className="flex items-center justify-between w-full">
                    <span className="flex items-center gap-1.5 text-xs text-txt-muted">
                      <Calendar size={13} />
                      {new Date(vid.createdAt).toLocaleDateString('pt-BR')}
                    </span>
                    <div className="flex items-center gap-2">
                      <EduButton
                        variant="secondary"
                        size="sm"
                        onClick={() => setSelectedVideo(vid)}
                        icon={<Play size={12} />}
                      >
                        Assistir
                      </EduButton>
                      <a
                        href={formatMediaUrl(vid.videoUrl)}
                        download
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 text-txt-secondary hover:text-brand-primary rounded transition-colors"
                        title="Baixar MP4"
                      >
                        <Download size={16} />
                      </a>
                    </div>
                  </div>
                }
              >
                {/* Badges de Projeto & Aula */}
                <div className="mb-3 space-y-1 bg-surface-secondary/70 p-2.5 rounded-lg border border-bdr text-xs">
                  <div className="flex items-center gap-1.5">
                    <FolderKanban size={13} className="text-brand-primary shrink-0" />
                    <span className="text-txt-muted text-[11px]">Projeto:</span>
                    <strong className="text-txt-primary truncate">
                      {vid.projectName || 'Projeto Não Especificado'}
                    </strong>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <BookOpen size={13} className="text-brand-primary shrink-0" />
                    <span className="text-txt-muted text-[11px]">Aula:</span>
                    <span className="text-txt-primary font-medium truncate" title={vid.lessonTitle}>
                      {vid.lessonTitle || `Aula #${vid.lessonId.substring(0, 8)}`}
                    </span>
                  </div>
                </div>

                {/* Thumbnail banner */}
                <div
                  onClick={() => setSelectedVideo(vid)}
                  className="h-36 bg-brand-graphite rounded-lg flex items-center justify-center cursor-pointer group relative overflow-hidden mb-3"
                >
                  <div className="w-12 h-12 rounded-full bg-brand-primary/90 text-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Play size={20} className="ml-0.5" />
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] px-2 py-0.5 rounded font-mono">
                    {Math.floor(vid.durationSeconds / 60)}:
                    {(vid.durationSeconds % 60).toString().padStart(2, '0')}
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-txt-secondary">
                  <span>Tamanho: {(vid.fileSize / (1024 * 1024)).toFixed(1)} MB</span>
                  <EduBadge variant="success" size="sm" dot>
                    MP4 H.264
                  </EduBadge>
                </div>
              </EduCard>
            ))}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl border border-bdr shadow-2xl max-w-3xl w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-bdr flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-txt-primary">
                  {selectedVideo.lessonTitle || 'Reprodução da Videoaula'}
                </h3>
                <p className="text-xs text-txt-secondary mt-0.5 flex items-center gap-2">
                  {selectedVideo.projectName && (
                    <>
                      <span className="inline-flex items-center gap-1 text-brand-primary font-semibold">
                        <FolderKanban size={12} />
                        {selectedVideo.projectName}
                      </span>
                      <span>•</span>
                    </>
                  )}
                  <span>{selectedVideo.resolution}</span>
                  <span>•</span>
                  <span>
                    {Math.floor(selectedVideo.durationSeconds / 60)}:
                    {(selectedVideo.durationSeconds % 60).toString().padStart(2, '0')}
                  </span>
                </p>
              </div>
              <button
                onClick={() => setSelectedVideo(null)}
                className="p-1.5 text-txt-secondary hover:text-txt-primary rounded"
              >
                <X size={18} />
              </button>
            </div>

            <div className="bg-black aspect-video flex items-center justify-center">
              <video
                src={formatMediaUrl(selectedVideo.videoUrl)}
                controls
                autoPlay
                className="w-full h-full object-contain"
              />
            </div>

            <div className="px-6 py-4 bg-surface-secondary flex items-center justify-between text-xs">
              <span className="text-txt-secondary">Formato: {selectedVideo.format.toUpperCase()} (Codec H.264 / AAC)</span>
              <a
                href={formatMediaUrl(selectedVideo.videoUrl)}
                download
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-primary text-white rounded-md font-semibold text-xs hover:bg-brand-hover shadow-sm"
              >
                <Download size={14} /> Baixar Arquivo MP4
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
